import {
  RecommendPanel,
  RecommendTypes,
} from '@doda/common/src/interfaces/api/recommendPanel';
import { adobeAnalyticsScriptsSelector } from '@doda/common/src/redux/features/analytics';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { measureVisibleRecommend } from 'pages/DodaFront/View/JobSearchDetail/logics/analytics';
import { Tab } from 'pages/DodaFront/View/JobSearchDetail/types/tab';
import { createRef, RefObject, useEffect, useState } from 'react';

interface Props {
  recommendList: RecommendPanel[];
  currentTabName: Tab;
}

/**
 * レコメンドVisible計測のみカスタムフックに切り出し
 * @param recommendList レコメンドリスト
 * @param currentTabName 現在のタブ
 * @returns refs
 */
export const useRecommendVisibleAnalytics = ({
  recommendList,
  currentTabName,
}: Props) => {
  const dispatch = useAppDispatch();
  const isAdobeScript = useAppSelector(adobeAnalyticsScriptsSelector);
  const [refs, setRefs] = useState<
    { ref: RefObject<HTMLDivElement>; isInView: boolean }[]
  >([]);

  useEffect(() => {
    recommendList.forEach(() => {
      setRefs((prev) => {
        return [
          ...prev,
          {
            ref: createRef<HTMLDivElement>(),
            isInView: false,
          },
        ];
      });
    });
  }, [recommendList]);

  useEffect(() => {
    if (!isAdobeScript) return undefined;
    const observer = new IntersectionObserver(
      ([{ isIntersecting, target }]) => {
        if (!isIntersecting) return;

        // entryからdata-recommendTypeを取得
        const recommendType =
          target instanceof HTMLElement
            ? (target.dataset.recommendType as RecommendTypes)
            : '';
        if (!recommendType) return;

        const intersectingRef = refs.find(
          ({ ref }) => ref.current?.dataset.recommendType === recommendType
        );
        // 発火済みの場合は抜ける
        if (!intersectingRef || intersectingRef.isInView) return;

        measureVisibleRecommend(
          isAdobeScript,
          recommendType,
          dispatch,
          formatJobIdList(recommendList, recommendType),
          currentTabName
        );
        intersectingRef.isInView = true;
      }
    );

    refs.forEach(({ ref }) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => {
      refs.forEach(({ ref }) => {
        if (ref.current) {
          observer.unobserve(ref.current);
        }
      });
    };
    // 不要なobserveを回避するため
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refs, isAdobeScript]);

  return { refs };
};

/**
 * レコメンドリストをjobIdの配列に変換する（計測用）
 * @param recommendList
 * @param recommendType
 */
function formatJobIdList(
  recommendList: RecommendPanel[],
  recommendType: RecommendTypes
) {
  return recommendList
    .filter((recommend) => recommend.recommendType === recommendType)
    .flatMap(({ results }) => results.map(({ itemNo }) => itemNo));
}
